import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { RelatedProductsService } from '@df/catalog-ui/related-products/related-products.service';
import { IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { AbIfService } from '@df/module-ab/ab-if.service';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { KlevuTrackService } from '@df/module-klevu-search/klevu-track.service';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import findIndex from 'lodash-es/findIndex';
import {
    addKlevuContextRecommendationsVariant,
    addKlevuRecommendationsVariant
} from 'projects/app-default/src/app/module/klevu-search/klevu-recommendations.config';
import { Product } from '../../../product/product.decorator';
import { ProductComponent } from '../../../product/product/product.component';
import { ModelCarouselComponentDef } from '../model-carousel/model-carousel.component.def';
import { getFields } from '../model-carousel/model-carousel.config';
import { IModelCarouselData, IModelCarouselLoadRequest } from '../model-carousel/model-carousel.interface';
export interface IPageComponentProductCarouselData extends IModelCarouselData, KlevuSearch.IRecommendationsModelCarouselLoadRequest {
    carouselVariant: string;
    showCarouselOnFacets?: string;
    attribute?: string;
    klevuRecommendationId?: string;
    strategyId: string;
    isRecentlyViewed?: boolean;
    variant: any;
}

let uid = 0;

const fields = addKlevuContextRecommendationsVariant(addKlevuRecommendationsVariant(getFields('product')));
const useProductsIdsFromPdp = (data: KlevuSearch.IRecommendationsModelCarouselLoadRequest) => {
    if (typeof window !== 'undefined' && window?.app?.meta?.model?.type === 'product') {
        return data?.klevuTypeId === KlevuSearch.ERecommendationTypeId.SimilarProducts;
    }
    return false;
};
const klevuProductsFieldIndex = findIndex(fields, i => i.key === 'klevuProductsIds');
if (klevuProductsFieldIndex > -1) {
    const field = fields[klevuProductsFieldIndex];
    if (field) {
        const coreCanShow = field.canShow;
        field.canShow = (
            data: KlevuSearch.IRecommendationsModelCarouselLoadRequest,
            outletOptions: NICmsContentComponent.IOutletOptions
        ) => {
            if (coreCanShow && !coreCanShow(data, outletOptions)) {
                return false;
            }
            if (useProductsIdsFromPdp(data)) {
                return false;
            }
            return true;
        };
    }
}
const variantField = fields.find(i => i.key === 'variant') as NICmsContentField.IChoiceField;
if (Array.isArray(variantField.options)) {
    variantField.options.push({ key: 'attribute', title: 'Range' });
}
@Component({
    selector: 'products-carousel',
    templateUrl: 'products-carousel.html',
    styleUrls: ['products-carousel.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        SizeClassDirective,
        CommonModule,
        ProductComponent,
        GridModule,
        ICmsClientModule,
        TranslationModule,
        CarouselModule,
        ProductComponent
    ]
})
@ICmsComponent<PageComponentProductsCarouselComponent>({
    id: 'product_carousel_layout',
    name: 'Product Carousel Layout',
    tags: ['Product', 'Carousel'],
    fields: [
        <NICmsContentField.IChoiceField>{
            key: 'carouselVariant',
            type: NICmsContentField.EFieldType.choice,
            title: 'Carousel Variant',
            required: true,
            default: 'full_details',
            options: [
                { key: 'full_details', title: 'Full details carousel' },
                { key: 'reduced_details', title: 'Reduced details carousel' },
                { key: 'smaller_squares', title: 'Smaller squares' },
                { key: 'smaller_squares_title', title: 'Smaller squares with title' },
                { key: 'new_in', title: 'New in' }
            ]
        },
        <NICmsContentField.IChoiceField>{
            key: 'showCarouselOnFacets',
            type: NICmsContentField.EFieldType.choice,
            title: 'Show carousel on facets',
            default: 'show',
            options: [
                { key: 'show', title: 'Show' },
                { key: 'hide', title: 'Hide' }
            ]
        },
        <NICmsContentField.IChoiceField>{
            key: 'attribute',
            type: NICmsContentField.EFieldType.choice,
            title: 'Additional attribute',
            options: () => {
                if (typeof window !== 'undefined') {
                    return [{ key: undefined, value: undefined, title: 'undefined' }, ...getAttributeOptions()];
                }
                return [];
            },
            canShow: (data: IModelCarouselData) => (data?.variant as string) === 'attribute'
        },
        ...fields
    ]
})
export class PageComponentProductsCarouselComponent
    extends ModelCarouselComponentDef<Product, IPageComponentProductCarouselData>
    implements OnInit
{
    @ICmsContent({
        type: 'component'
    })
    override component?: IPageComponentProductCarouselData;

    uid = `products-carousel-${uid++}`;

    protected override canDetach = false;

    type = 'product';
    private klevuTrackService = inject(KlevuTrackService);

    relatedPrroductsService = inject(RelatedProductsService);

    app = inject(App);

    private abIfService = inject(AbIfService);

    experimentModelsLength = 12;
    disabled = false;

    displayedModels?: Product[];

    override ngOnInit(): void {
        super.ngOnInit();
        if (this.app.meta.model?.type && ['product', 'category'].indexOf(this.app.meta.model.type) !== -1) {
            this.subscriptions.add(
                this.app.meta.subscribe(() => {
                    const promise = this.app.meta.model?.load(2);
                    promise && promise.then(() => this.setModels());
                    this.detectChanges();
                })
            );
            this.subscriptions.add(
                this.abIfService.getState('TARGETING').subscribe(() => {
                    this.onModelsSet();
                })
            );
        }
    }

    loadMoreModels() {
        if (this.disabled) {
            return;
        }
        const filteredModels = this.filteredModels;
        const experimentModelsLength = this.experimentModelsLength;
        if (experimentModelsLength + 6 > filteredModels.length) {
            this.experimentModelsLength = experimentModelsLength + (filteredModels.length - this.experimentModelsLength);
            this.disabled = true;
        } else {
            this.experimentModelsLength = experimentModelsLength + 6;
        }

        this.detectChanges();
    }

    get filteredModels() {
        return (this.listing?.models as Product<IProductDataV2>[])?.filter(model => !!model?.data?.url);
    }

    get showExperimentalVersion() {
        return (
            this.component?.variant === 'klevuContextRecommendations' &&
            (this.client.isS || this.client.isM) &&
            this.app.ab.getVariant('TARGETING') === 'B'
        );
    }

    get isKlevuRecomendations(): boolean {
        const variant = this.component?.variant as string;
        return [KlevuSearch.contextRecommendationsEngineId].indexOf(variant) !== -1;
    }

    get klevuTypeId(): string | undefined {
        if (this.isKlevuRecomendations) {
            return this.component?.klevuTypeId;
        }
    }

    get klevuRecommendationId(): string | undefined {
        if (this.isKlevuRecomendations) {
            return this.component?.klevuRecommendationId;
        }
    }

    get isFacet(): boolean {
        return !!this.app.meta.state?.route?.data?.facet?.category;
    }

    protected override onModelsSet(): void {
        if (this.isKlevuRecomendations && this.component) {
            const products = this.listing?.getSourceModels() as Product<IProductDataV2>[];
            if (products?.length) {
                const klevuRecommendationId = this.klevuRecommendationId;
                if (klevuRecommendationId) {
                    this.klevuTrackService.trackRecommendation({
                        eventListId: klevuRecommendationId,
                        event: KlevuSearch.EAnalyticsRecommendationEvent.view,
                        products: products.map((product, index) => {
                            return {
                                model: product,
                                position: index
                            };
                        })
                    });
                }
            }
        }
        const filteredModels = this.filteredModels;
        if (this.showExperimentalVersion) {
            this.displayedModels = filteredModels.slice(0, this.experimentModelsLength) || [];
        } else {
            this.displayedModels = filteredModels || [];
        }
        this.detectChanges();
    }
    get listData(): string {
        return this.component?.title + ':' + this.app.meta.state?.url;
    }
    protected override getLoadRequest(): IModelCarouselLoadRequest | undefined {
        const loadRequest = { ...super.getLoadRequest() } as IModelCarouselLoadRequest;
        if ((this.component?.variant as string) === 'attribute') {
            const model = this.app.meta.model;
            if (model?.type === 'product') {
                loadRequest.ids = [model?.id as number];
                loadRequest['attributeCode'] = ['nyw_item_range_code_nh', this.component?.attribute].filter(i => !!i);
            }
        }
        return loadRequest;
    }
}

function getAttributeOptions() {
    return (
        (<App>window['app'])?.resources?.attribute?.getAllExisting().map(i => {
            return {
                key: i.id,
                value: i.id,
                title: i.data?.label
            };
        }) || []
    );
}
